<script setup>
import { tv } from 'tailwind-variants';
import { computed } from 'vue';
import { CsTransitionChild } from '../transition';
import { useModalContext } from './useModal';

const props = defineProps({
  enableVerticalScroll: {
    type: Boolean,
    default: false,
  },
});

const { rootProps, hasModalHeader, hasModalFooter } = useModalContext();

const size = computed(() => rootProps.value?.size);
const verticalPadding = computed(() => {
  if (rootProps.value.verticalPadding === 'default') {
    return '160';
  }

  return '40';
});

const panelThemes = tv({
  slots: {
    root: 'fixed inset-0 z-[var(--modal-z-index)] w-screen h-screen pointer-events-none',
    container:
      'flex min-h-screen items-end justify-center text-center sm:items-center',
    content:
      'pointer-events-auto text-start relative transform rounded-lg bg-brand-background shadow-brand-600 transition-all border border-brand-gray-8 dark:border-brand-gray-15',
  },
  variants: {
    size: {
      sm: {
        content:
          'w-full sm:w-full sm:max-w-[380px] sm:max-h-[calc(100vh-var(--vertical-padding))]',
        container: 'px-10',
      },
      md: {
        content:
          'w-full sm:w-full sm:max-w-[480px] sm:max-h-[calc(100vh-var(--vertical-padding))]',
        container: 'px-10',
      },
      lg: {
        content:
          'w-full sm:w-full sm:max-w-[620px] sm:max-h-[calc(100vh-var(--vertical-padding))]',
        container: 'px-10',
      },
      xl: {
        content:
          'w-full sm:w-full sm:max-w-[980px] sm:max-h-[calc(100vh-var(--vertical-padding))]',
        container: 'px-10',
      },
      '2xl': {
        content:
          'w-full h-screen sm:h-[calc(100vh-var(--vertical-padding))] sm:w-full sm:max-w-full',
        container: 'sm:px-16',
      },
      full: {
        content:
          'w-full h-screen sm:h-[calc(100vh-var(--vertical-padding))] sm:w-full sm:max-w-full',
        container: 'sm:px-6',
      },
    },
    enableVerticalScroll: {
      true: {
        content: 'overflow-y-auto',
      },
      false: {
        content: 'overflow-y-hidden',
      },
    },
  },
  defaultVariants: {
    size: 'lg',
    enableVerticalScroll: false,
  },
});
</script>

<template>
  <div
    data-slot="panel"
    :style="{
      '--vertical-padding': `${size === 'full' ? '48' : verticalPadding}px`,
      '--modal-header-height': hasModalHeader ? '45px' : '0px',
      '--modal-footer-height': hasModalFooter ? '60px' : '0px',
    }"
    :class="
      panelThemes({
        size,
        enableVerticalScroll: props.enableVerticalScroll,
      }).root()
    "
  >
    <div
      data-slot="panel-container"
      :class="
        panelThemes({
          size,
          enableVerticalScroll: props.enableVerticalScroll,
        }).container()
      "
    >
      <CsTransitionChild
        enter-active-class="transition ease-out duration-300"
        enter-class="opacity-0 translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-200"
        leave-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-4"
      >
        <div
          data-slot="panel-content"
          :class="
            panelThemes({
              size,
              enableVerticalScroll: props.enableVerticalScroll,
            }).content()
          "
        >
          <slot />
        </div>
      </CsTransitionChild>
    </div>
  </div>
</template>
